import React, {
  ChangeEvent,
  FunctionComponent,
  useState,
  useContext,
  useMemo,
  useCallback,
  ReactElement,
  useEffect,
  forwardRef,
} from "react";
import styles from "./ThreadCard.module.css";
import clsx from "clsx";
import {
  Button,
  Card,
  Collapse,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import { Clear, ExpandMore } from "@mui/icons-material";
import AppStateContext from "../../../contexts/AppStateContext";
import { ThreadCardInfo } from "../../../types/ConfigTypes";
import { ThreadsType } from "../../../types/ThreadsType";
import { SelectedThreadsInfoType } from "../../../types/RenderTypes";

type IndividualHoleSelectorPropsType = {
  groupIds: number[];
  holeIds: number[];
  threadSelection: ReactElement[][];
  selectedOption: string;
  selectedThreadId: number;
  setSelectedOptions: (option: string, index?: number) => void;
  setHover?: (ids: number[]) => void;
};

const IndividualHoleSelector: FunctionComponent<
  IndividualHoleSelectorPropsType
> = ({
  groupIds,
  holeIds,
  threadSelection,
  selectedOption,
  selectedThreadId,
  setSelectedOptions,
  setHover,
}) => {
  const { strings } = useContext(AppStateContext);
  const [isHovered, setHovered] = useState<boolean>(false);

  const changeHover = useCallback(
    (ids: number[]) => {
      setHovered(ids.length > 0);
      if (setHover) {
        setHover(ids);
      }
    },
    [setHover]
  );

  const isCustom = useMemo(() => {
    return (
      selectedOption !== "none" &&
      selectedThreadId < 0 &&
      !threadSelection.flat().some((val) => val.props.value === selectedOption)
    );
  }, [selectedOption, selectedThreadId, threadSelection]);

  return isCustom ? (
    <TextField
      className={styles.formControlSecondary}
      InputProps={{
        className: isHovered ? styles.formControlSecondaryHover : "",
        classes: {
          notchedOutline: isHovered ? styles.formControlSecondaryHover : "",
        },
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              onClick={() => setSelectedOptions("none", holeIds[0])}
              edge="end"
            >
              <Clear />
            </IconButton>
          </InputAdornment>
        ),
      }}
      value={selectedOption}
      onChange={(event: any) => {
        setSelectedOptions(event.target.value, holeIds[0]);
      }}
      variant="outlined"
      size={"small"}
      margin={"dense"}
      onMouseEnter={(e: any) => {
        e.preventDefault();
        changeHover && changeHover(holeIds);
      }}
      onMouseLeave={(e: any) => {
        e.preventDefault();
        changeHover && changeHover(groupIds);
      }}
    />
  ) : (
    <TextField
      className={styles.formControlSecondary}
      InputProps={{
        className: isHovered ? styles.formControlSecondaryHover : "",
        classes: {
          notchedOutline: isHovered ? styles.formControlSecondaryHover : "",
        },
      }}
      select
      value={selectedThreadId > 0 ? selectedThreadId : selectedOption}
      onChange={(event: any) => {
        setSelectedOptions(event.target.value);
      }}
      SelectProps={{
        native: true,
      }}
      variant="outlined"
      size={"small"}
      margin={"dense"}
      onMouseEnter={(e: any) => {
        e.preventDefault();
        changeHover && changeHover(holeIds);
      }}
      onMouseLeave={(e: any) => {
        e.preventDefault();
        changeHover && changeHover(groupIds);
      }}
    >
      <option value={"none"}>{`\xa0 ${strings.ThreadCardNoThreads}`}</option>
      <option
        value={""}
        style={{ cursor: "pointer", color: "#008b80" }}
        id="customThreadSelectOption"
      >{` \xa0 ${strings.AddCustomThread}`}</option>
      {threadSelection}
    </TextField>
  );
};

type PropsType = {
  data: ThreadCardInfo;
  selections: SelectedThreadsInfoType[];
  onSelectionsChange: (updated: SelectedThreadsInfoType[]) => void;
  hover?: boolean;
  setHover?: (ids: number[]) => void;
};

const ThreadCard = forwardRef<any, PropsType>(
  ({ data, selections, onSelectionsChange, hover, setHover }, ref) => {
    const { strings } = useContext(AppStateContext);

    const singleHole = useMemo(() => data.holes.length <= 1, [data.holes]);
    const [expanded, setExpanded] = useState(false);
    const [isHovered, setHovered] = useState<boolean>(false);

    // Build the effective selections: one entry per hole, defaulting to "none"
    const effectiveSelections = useMemo(() => {
      return data.holes.map((hole) => {
        const existing = selections.find((s) => s.id === hole.id);
        if (existing) {
          if (!existing.threadId) {
            const thread = data.threads.find((t) => t.name === existing.name);
            if (thread) {
              return { ...hole, threadId: thread.id, name: existing.name };
            }
          }
          return { ...hole, name: existing.name, threadId: existing.threadId };
        }
        return { ...hole, name: "none", threadId: -1 };
      });
    }, [data.holes, data.threads, selections]);

    const getSelectedOption = useCallback(
      (id: number) => {
        return effectiveSelections.find((val) => val.id === id);
      },
      [effectiveSelections]
    );

    useEffect(() => {
      setHovered(hover ?? false);
    }, [hover]);

    const changeHover = useCallback(
      (ids: number[]) => {
        setHovered(ids.length > 0);
        if (setHover) {
          setHover(ids);
        }
      },
      [setHover]
    );

    const handleClear = () => {
      const updatedOptions = effectiveSelections.map((item) => ({
        ...item,
        name: "none",
        threadId: -1,
      }));
      onSelectionsChange(updatedOptions);
    };

    const threadsByCategories = useMemo(() => {
      const threadsByCategories: { [key: string]: ThreadsType[] } = {};
      data.threads.forEach((thread) => {
        if (!threadsByCategories[thread.category]) {
          threadsByCategories[thread.category] = [];
        }
        if (thread.category === "Tolerance") {
          if (data.type === "shaft") {
            threadsByCategories[thread.category].push({
              ...thread,
              name: thread.name.toLowerCase(),
            });
          } else {
            threadsByCategories[thread.category].push(thread);
          }
        } else {
          threadsByCategories[thread.category].push(thread);
        }
      });
      return threadsByCategories;
    }, [data.threads, data.type]);

    const threadSelection = useMemo(() => {
      return Object.entries(threadsByCategories).map(([objKey, cat]) => [
        <option disabled={true} key={objKey} value={objKey}>
          {objKey}
        </option>,
        ...cat.map((val, key) => (
          <option key={key} value={val.id}>
            {`\xa0 ${val.name}`}
          </option>
        )),
      ]);
    }, [threadsByCategories]);

    const allSelector = useMemo(() => {
      const mixed =
        effectiveSelections.find(
          (val) => val.name !== effectiveSelections[0].name
        ) !== undefined;
      let custom = false;
      if (!mixed) {
        const holeName = effectiveSelections[0].name;
        const exists = Object.values(threadsByCategories)
          .flat()
          .some((item) => item.name == holeName);
        custom = holeName !== undefined && holeName !== "none" && !exists;
      }
      return custom ? (
        <TextField
          id={"Selector"}
          className={styles.formControl}
          InputProps={{
            className: styles.formControlInput,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleClear} edge="end">
                  <Clear />
                </IconButton>
              </InputAdornment>
            ),
          }}
          value={effectiveSelections[0].name}
          onChange={(event: ChangeEvent<{ value: string }>) => {
            const updatedOptions = effectiveSelections.map((op) => ({
              ...op,
              name: event.target.value,
              threadId: -1,
            }));
            onSelectionsChange(updatedOptions);
          }}
          variant="outlined"
          size={"small"}
        />
      ) : (
        <TextField
          id={"Selector"}
          className={styles.formControl}
          InputProps={{
            className: styles.formControlInput,
          }}
          select
          value={mixed ? "mixed" : effectiveSelections[0].threadId || effectiveSelections[0].name}
          onChange={(event: ChangeEvent<{ value: string }>) => {
            const selectedValue = event.target.value;
            if (selectedValue === "none" || selectedValue === "") {
              const updatedOptions = effectiveSelections.map((op) => ({
                ...op,
                name: selectedValue === "" ? "" : "none",
                threadId: -1,
              }));
              onSelectionsChange(updatedOptions);
            } else {
              const numId = Number(selectedValue);
              const thread = data.threads.find((t) => t.id === numId);
              if (thread) {
                const updatedOptions = effectiveSelections.map((op) => ({
                  ...op,
                  name: thread.name,
                  threadId: thread.id,
                }));
                onSelectionsChange(updatedOptions);
              } else {
                const updatedOptions = effectiveSelections.map((op) => ({
                  ...op,
                  name: selectedValue,
                  threadId: -1,
                }));
                onSelectionsChange(updatedOptions);
              }
            }
          }}
          SelectProps={{
            native: true,
          }}
          variant="outlined"
          size={"small"}
        >
          {mixed ? (
            <option value={"mixed"} disabled={true}>
              {`\xa0 ${strings.ThreadCardMixedThreads}`}
            </option>
          ) : null}
          <option
            value={"none"}
          >{`\xa0 ${strings.ThreadCardNoThreads}`}</option>
          <option
            value={""}
            style={{ cursor: "pointer", color: "#008b80" }}
            id="customThreadAllSelect"
          >{`\xa0 ${strings.AddCustomThread}`}</option>
          {threadSelection}
        </TextField>
      );
    }, [
      effectiveSelections,
      strings.ThreadCardMixedThreads,
      strings.ThreadCardNoThreads,
      threadSelection,
      onSelectionsChange,
      data.holes,
      data.threads,
      threadsByCategories,
    ]);

    const individualHolesSelectors = useMemo(() => {
      return data.holes.map((hole, key) => (
        <IndividualHoleSelector
          key={key}
          groupIds={data.holes.map((h) => h.id)}
          holeIds={hole.faceIds}
          threadSelection={threadSelection}
          selectedOption={getSelectedOption(hole.id)?.name ?? "none"}
          selectedThreadId={getSelectedOption(hole.id)?.threadId ?? -1}
          setSelectedOptions={(val: string, index?: number) => {
            if (index !== undefined) {
              // Custom thread text input
              const updated = effectiveSelections.map((s) =>
                s.id === hole.id ? { ...s, name: val, threadId: -1 } : s
              );
              onSelectionsChange(updated);
            } else {
              // From dropdown - val is now the thread DB id
              const numId = Number(val);
              const thread = data.threads.find((t) => t.id === numId);
              if (thread) {
                const updated = effectiveSelections.map((s) =>
                  s.id === hole.id ? { ...s, name: thread.name, threadId: thread.id } : s
                );
                onSelectionsChange(updated);
              } else {
                // "none" or custom
                const updated = effectiveSelections.map((s) =>
                  s.id === hole.id ? { ...s, name: val, threadId: -1 } : s
                );
                onSelectionsChange(updated);
              }
            }
          }}
          setHover={setHover}
        />
      ));
    }, [
      data.holes,
      data.threads,
      getSelectedOption,
      setHover,
      threadSelection,
      effectiveSelections,
      onSelectionsChange,
    ]);

    return (
      <div
        className={`${styles.container} ${isHovered && styles.containerHover}`}
        onMouseEnter={(e: any) =>
          changeHover(data.holes.map((hole) => hole.faceIds).flat())
        }
        onMouseLeave={(e: any) => changeHover([])}
      >
        <Card
          //@ts-ignore
          ref={ref}
          className={`${styles.root} ${isHovered && styles.rootHover}`}
        >
          <div className={styles.cardContent}>
            <div className={styles.cardHeader}>
              <div>
                <div className={styles.headerText}>
                  {strings.ThreadCardDiameter}
                </div>
                <div className={styles.headerValue}>
                  {data.diameter}
                  {strings.Milimeter}
                </div>
              </div>
              <div>
                <div className={styles.headerText}>
                  {strings.ThreadCardDepth}
                </div>
                <div className={styles.headerValue}>
                  {data.depth}
                  {strings.Milimeter}
                </div>
              </div>
              <div>
                <div className={styles.headerText}>
                  {strings.ThreadCardQuantity}
                </div>
                <div className={styles.headerValue}>
                  {0}/{data.holes.length}
                </div>
              </div>
            </div>
            {!singleHole && allSelector}
          </div>
          {!singleHole ? (
            <>
              <div
                style={{
                  marginTop: "1em",
                }}
                className={styles.expandContainer}
                onClick={() => setExpanded(!expanded)}
              >
                <Button
                  aria-expanded={expanded}
                  aria-label="show more"
                  color={"primary"}
                  startIcon={
                    <ExpandMore
                      className={clsx(styles.expand, {
                        [styles.expandOpen]: expanded,
                      })}
                    />
                  }
                >
                  {strings.ThreadCardSpecify}
                </Button>
              </div>

              <Collapse in={expanded} timeout="auto" unmountOnExit>
                <div className={styles.formControlSecondaryContainer}>
                  {individualHolesSelectors}
                </div>
              </Collapse>
            </>
          ) : (
            individualHolesSelectors
          )}
        </Card>
      </div>
    );
  }
);
export default ThreadCard;
